import React, { useContext, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { AlertTriangle, Filter, ArrowUpNarrowWide, ArrowDownWideNarrow } from "lucide-react";
import { getAllOrderDetail } from "../../reducers/order.slice";
import {
  convertDateFormatInUtc,
  formatNumberToDollar,
  countActiveFiltersAndSort,
  subtractHoursUtc,
} from "../../utils/util";
import { AuthContext } from "../../context/authContext";
import { Button } from "../../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "../../components/ui/dropdown-menu";
import PageHeader from "../../components/shared/PageHeader";
import StatusBadge from "../../components/shared/StatusBadge";
import StatCard from "../../components/shared/StatCard";
import SearchBar from "../../components/shared/SearchBar";
import RefreshButton from "../../components/shared/RefreshButton";
import DataTable from "../../components/shared/DataTable";
import useMediaQuery from "../../components/shared/useMediaQuery";

const TYPE_OPTIONS = [
  { value: "PRE_QUOTE", label: "Sell" },
  { value: "QUOTE", label: "Quote" },
  { value: "SHIP_DIRECT", label: "Ship Direct" },
];
const ORDER_TYPE_VALUES = TYPE_OPTIONS.map((option) => option.value);

const getStoredOrderTypes = () => {
  const stored = sessionStorage.getItem("openOrdersMember_selectedTypes");
  if (!stored) return [];
  const parsed = JSON.parse(stored);
  return Array.isArray(parsed) ? parsed.filter((type) => ORDER_TYPE_VALUES.includes(type)) : [];
};

const getOrderTypeLabel = (type) =>
  TYPE_OPTIONS.find((option) => option.value === type)?.label ||
  type?.replace(/_/g, " ") ||
  "—";

// Quick-filter tabs cover every real order status (PENDING_APPROVAL, APPROVED,
// REJECTED — confirmed against backend/order_mgmt's OrderStatus enum, there
// is no fourth "EXPIRED" status), so — unlike Shipments' tabs, which leave a
// few statuses for the Filter dropdown — there's nothing left over here; the
// Filter dropdown only needs Type + Sort.
const TAB_OPTIONS = [
  { key: "all", label: "All", statuses: [] },
  { key: "pending", label: "Pending Approval", statuses: ["PENDING_APPROVAL"] },
  { key: "approved", label: "Approved", statuses: ["APPROVED"] },
  { key: "rejected", label: "Rejected", statuses: ["REJECTED"] },
];
// Caps the sample used to sum value/units for the strip (no aggregate endpoint
// exists) — generous for a single member's order volume.
const ORDER_STATS_SAMPLE_LIMIT = 200;

const MemberOpenOrderPage = () => {
  const [showLoader, setShowLoader] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState(() => {
    const getStoredStatuses = sessionStorage.getItem("openOrdersMember_selectedStatuses");
    return getStoredStatuses ? JSON.parse(getStoredStatuses) : [];
  });
  const [activeTab, setActiveTab] = useState(() => {
    const stored = sessionStorage.getItem("openOrdersMember_selectedStatuses");
    const storedStatuses = stored ? JSON.parse(stored) : [];
    const match = TAB_OPTIONS.find(
      (tab) =>
        tab.statuses.length === storedStatuses.length &&
        tab.statuses.every((s) => storedStatuses.includes(s))
    );
    return match ? match.key : "all";
  });
  const [selectedTypes, setSelectedTypes] = useState(() => {
    return getStoredOrderTypes();
  });
  const [sortDirection, setSortDirection] = useState(() => {
    return sessionStorage.getItem("openOrdersMember_sortDirection") || null;
  });
  const [allLoadedData, setAllLoadedData] = useState([]);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [loadingChunk, setLoadingChunk] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const getRowsPerPage = sessionStorage.getItem("openOrdersMember_rowsPerPage");
    return getRowsPerPage ? JSON.parse(getRowsPerPage) : 5;
  });
  const [totalRows, setTotalRows] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isNavigatingToPage, setIsNavigatingToPage] = useState(false);
  const [pageCache, setPageCache] = useState(new Map());
  const [filterOpen, setFilterOpen] = useState(false);
  const [orderStats, setOrderStats] = useState(null);
  const CHUNK_SIZE = 50;
  const isInitialRender = useRef(true);

  const dispatch = useDispatch();
  const isMobile = useMediaQuery("(max-width: 640px)");

  const { showToast } = useContext(AuthContext);

  // Fixed overview strip, same regardless of the active status tab — no
  // aggregate endpoint exists, so this is a capped sample like the rest of
  // the member portal's stat strips.
  useEffect(() => {
    dispatch(
      getAllOrderDetail({
        skip: 0,
        limit: ORDER_STATS_SAMPLE_LIMIT,
        order_status: [],
        type: [],
      })
    ).then((res) => {
      const orders = res?.payload?.data || [];
      const totalValue = orders.reduce((sum, o) => sum + (o.order_price || 0), 0);
      const units = orders.reduce(
        (sum, o) => sum + (o.order_items || []).reduce((s, item) => s + (item.quantity || 0), 0),
        0
      );
      const pendingApprovalCount = orders.filter((o) => o.status === "PENDING_APPROVAL").length;

      setOrderStats({
        totalOrders: res?.payload?.total ?? orders.length,
        totalValue,
        units,
        pendingApprovalCount,
      });
    });
  }, [dispatch]);

  const loadDataChunk = async (chunkNumber = 0, overrides = {}) => {
    // overrides lets a tab click apply immediately without waiting for the
    // setSelectedStatuses state update to land (avoids a stale-closure read
    // on the very first fetch after the click) — same pattern used on the
    // Shipments page.
    const effectiveStatuses = overrides.statuses ?? selectedStatuses;
    // Create consistent filter key (sort arrays for stable keys)
    const filterKey = JSON.stringify({
      selectedStatuses: [...effectiveStatuses].sort(),
      selectedTypes: [...selectedTypes].sort(),
      search: searchTerm,
      sortDirection: sortDirection,
    });
    const cacheKey = `chunk-${chunkNumber}-${filterKey}`;

    // Check cache first
    if (!overrides.skipCache && pageCache.has(cacheKey)) {
      const cachedData = pageCache.get(cacheKey);
      if (cachedData.totalRows !== undefined) {
        setTotalRows(cachedData.totalRows);
      }
      return cachedData.data;
    }

    // Prevent multiple simultaneous calls
    if (loadingChunk) {
      return [];
    }

    setLoadingChunk(true);
    if (chunkNumber === 0 && allLoadedData.length === 0) {
      setShowLoader(true);
    }

    try {
      const payload = {
        skip: chunkNumber * CHUNK_SIZE,
        limit: CHUNK_SIZE,
        // Initialize arrays for multiple values
        order_status: [],
        type: [],
      };
      // Add multiple status filters as separate values
      if (effectiveStatuses && effectiveStatuses.length > 0) {
        payload.order_status = effectiveStatuses; // This will be sent as multiple params
      }

      // Add multiple type filters as separate values
      if (selectedTypes && selectedTypes.length > 0) {
        payload.type = selectedTypes; // This will be sent as multiple params
      }

      if (searchTerm && searchTerm?.trim() !== "") {
        payload.search = searchTerm?.trim();
      }
      if (sortDirection && sortDirection !== "null") {
        payload.sort_by = "ORDER_PRICE";
        payload.sort_order = sortDirection; // This will be sent as multiple params
      }
      const res = await dispatch(getAllOrderDetail(payload));
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setHasMoreData(false);
        setFilteredData([]);
        setShowLoader(false);
        showToast(res?.error?.message, "error");
        return;
      }
      if (
        !res?.payload ||
        !res?.payload?.data ||
        !Array.isArray(res.payload.data)
      ) {
        console.log("No valid data in response");
        setHasMoreData(false);
        setFilteredData([]);
        return;
      }

      if ((chunkNumber === 0 || !totalRows) && res?.payload?.total) {
        setTotalRows(res?.payload?.total);
      }

      const formattedOrder = res?.payload?.data?.map((item) => {
        const createdDate = convertDateFormatInUtc(
          item?.created_at,
          "MMM D, YYYY · h:mm A"
        );
        const approvalBaseDate =
          item.status === "APPROVED"
            ? item.status_updated_at || subtractHoursUtc(item.order_expiry)
            : null;
        const orderPrice = formatNumberToDollar(item?.order_price);
        const newOrderItems = item?.order_items?.map((orderItem) => {
          const buy_price = formatNumberToDollar(orderItem?.buy_price || 0);
          const cashout_price = formatNumberToDollar(
            orderItem?.cashout_price || 0
          );
          const total_amount = formatNumberToDollar(
            orderItem?.total_amount || 0
          );
          return {
            ...orderItem,
            buy_price,
            cashout_price,
            total_amount,
          };
        });

        return {
          ...item,
          newOrderItems,
          createdDate: createdDate,
          approvalDate: approvalBaseDate
            ? convertDateFormatInUtc(approvalBaseDate, "MMM D, YYYY · h:mm A")
            : "—",
          orderPrice,
        };
      });
      // Cache the chunk
      setPageCache(
        (prev) =>
          new Map(
            prev.set(cacheKey, {
              data: formattedOrder,
              timestamp: Date.now(),
              totalRows: res?.payload?.total,
            })
          )
      );

      const hasMore = formattedOrder?.length === CHUNK_SIZE;
      setHasMoreData(hasMore);
      return formattedOrder;
    } catch (error) {
      console.error("Error in loadDataChunk:", error);
      showToast("Error fetching data", "error");
      setHasMoreData(false);
      return [];
    } finally {
      setLoadingChunk(false);
      setShowLoader(false);
    }
  };

  const columns = [
    {
      key: "id",
      label: "Order ID",
      render: (row) => <span style={{ fontWeight: 700, color: "#fff" }}>{row.id}</span>,
    },
    {
      key: "orderPrice",
      label: "Order Price",
      render: (row) => <span style={{ fontWeight: 700, color: "#fff" }}>{row.orderPrice}</span>,
    },
    {
      key: "type",
      label: "Order Type",
      render: (row) => getOrderTypeLabel(row.type),
    },
    { key: "created_by_name", label: "Member Name" },
    { key: "createdDate", label: "Order Date" },
    { key: "approvalDate", label: "Approval Date" },
    {
      key: "status",
      label: "Status",
      render: (row) => <StatusBadge status={row.status} />,
    },
  ];

  const itemsColumns = [
    { key: "id", label: "Item ID" },
    { key: "item_name", label: "Item Name" },
    { key: "upc", label: "UPC" },
    { key: "quote_id", label: "Quote ID" },
    { key: "quantity", label: "Quantity" },
    { key: "buy_price", label: "Buy Price" },
    { key: "cashout_price", label: "Cashout Price" },
    { key: "retailer_name", label: "Retailer Name" },
    { key: "total_amount", label: "Total Amount" },
    { key: "notes", label: "Notes" },
    {
      key: "status",
      label: "Status",
      render: (row) => <StatusBadge status={row.status} />,
    },
  ];

  const handleTypeChange = (type, checked) => {
    setSelectedTypes((prev) => {
      return checked ? [...prev, type] : prev.filter((s) => s !== type);
    });
  };

  const applyFilters = async (overrides = {}) => {
    const effectiveStatuses = overrides.statuses ?? selectedStatuses;
    try {
      setLoadingChunk(true);
      setCurrentPage(0);
      setAllLoadedData([]);
      sessionStorage.setItem("openOrdersMember_selectedStatuses", JSON.stringify(effectiveStatuses));
      sessionStorage.setItem("openOrdersMember_selectedTypes", JSON.stringify(selectedTypes));
      sessionStorage.setItem("openOrdersMember_sortDirection", sortDirection);
      // Call API with new filters
      const results = await loadDataChunk(0, { statuses: effectiveStatuses });
      if (results?.length > 0) {
        setAllLoadedData(results);
      } else {
        setFilteredData([]);
        setAllLoadedData([]);
      }
    } catch (error) {
      console.error("Error applying filters:", error);
      showToast("Error applying filters", "error");
    } finally {
      setLoadingChunk(false);
    }
  };

  const handleTabClick = (tab) => {
    setActiveTab(tab.key);
    setSelectedStatuses(tab.statuses);
    applyFilters({ statuses: tab.statuses });
  };

  const handleFilterOpenChange = (nextOpen) => {
    setFilterOpen(nextOpen);
    if (!nextOpen) {
      applyFilters();
    }
  };

  const handleSortAscending = () => {
    setSortDirection((prev) => prev === "ASC" ? null : "ASC");
  };

  const handleSortDescending = () => {
    setSortDirection((prev) => prev === "DESC" ? null : "DESC");
  };

  const loadInitialData = async (overrides = {}) => {
    const initialChunk = await loadDataChunk(0, overrides);
    setAllLoadedData(initialChunk);
  };

  const checkAndLoadMoreData = async (page, rowsPerPage) => {
    const requiredDataLength = (page + 1) * rowsPerPage;
    const currentDataLength = allLoadedData.length;

    if (currentDataLength >= requiredDataLength) {
      console.log("Have enough data already");
      return;
    }

    if (!hasMoreData) {
      console.log("No more data available from server");
      return;
    }

    if (loadingChunk) {
      console.log("Already loading");
      return;
    }

    // CRITICAL: Check against total rows if available
    if (totalRows > 0 && currentDataLength >= totalRows) {
      setHasMoreData(false);
      return;
    }
    try {
      setIsNavigatingToPage(true);
      const nextChunk = Math.floor(currentDataLength / CHUNK_SIZE);
      const targetPage = page;
      const newData = await loadDataChunk(nextChunk);
      if (newData && newData?.length > 0) {
        setAllLoadedData((prev) => {
          const updated = [...prev, ...newData];
          return updated;
        });

        setTimeout(() => {
          const startIndex = page * rowsPerPage;
          const endIndex = startIndex + rowsPerPage;
          const combinedData = [...allLoadedData, ...newData];
          const pageData = combinedData.slice(startIndex, endIndex);
          setCurrentPage(targetPage);
          setFilteredData(pageData);
        }, 100);
      } else {
        console.log("No new data received, setting hasMoreData to false");
        setHasMoreData(false);
      }
    } catch (error) {
      console.error("Error loading more data:", error);
      showToast("Error loading data", "error");
    } finally {
      setIsNavigatingToPage(false);
    }
  };

  // FIXED: Handle page change with forced data update
  const handleChangePage = async (event, newPage) => {
    if (isNavigatingToPage || loadingChunk) return;

    const requiredDataLength = (newPage + 1) * rowsPerPage;

    try {
      setIsNavigatingToPage(true);

      if (allLoadedData.length < requiredDataLength && hasMoreData) {
        await checkAndLoadMoreData(newPage, rowsPerPage);
      } else {
        const startIndex = newPage * rowsPerPage;
        const endIndex = startIndex + rowsPerPage;
        const pageData = allLoadedData.slice(startIndex, endIndex);

        setCurrentPage(newPage);
        setFilteredData(pageData);
      }
    } catch (error) {
      console.error("Error changing page:", error);
      showToast("Error loading page data", "error");
    } finally {
      setIsNavigatingToPage(false);
    }
  };

  useEffect(() => {
    if (isNavigatingToPage || loadingChunk) {
      return;
    }

    if (!allLoadedData?.length) return;

    const startIndex = currentPage * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;

    const pageData = allLoadedData.slice(startIndex, endIndex);
    if (pageData.length > 0 && !isNavigatingToPage && !loadingChunk) {
      setFilteredData(pageData);
    } else {
      console.warn("No data available for current page:", currentPage);
      if (currentPage > 0) {
        console.log("Attempting to go back to previous page");
        setCurrentPage((prev) => prev - 1);
      }
    }
  }, [rowsPerPage, allLoadedData]);

  // Handle rows per page change
  const handleChangeRowsPerPage = async (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    sessionStorage.setItem("openOrdersMember_rowsPerPage", JSON.stringify(newRowsPerPage));
    setCurrentPage(0);

    const requiredDataLength = newRowsPerPage; // For page 0
    if (
      allLoadedData.length < requiredDataLength &&
      hasMoreData &&
      !loadingChunk
    ) {
      checkAndLoadMoreData(0, newRowsPerPage);
    }
  };

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }

    if (isSearching) return;

    const performSearch = async () => {
      try {
        setIsSearching(true);
        setCurrentPage(0);
        setAllLoadedData([]);

        const results = await loadDataChunk(0);
        if (results?.length > 0) {
          setAllLoadedData(results);
        } else {
          setFilteredData([]);
          setAllLoadedData([]);
        }
      } finally {
        setIsSearching(false);
      }
    };

    performSearch();
  }, [debouncedSearch]);

  const handleRefresh = () => {
    const currentPageBeforeRefresh = currentPage;

    setPageCache(new Map()); // Clear cache
    setAllLoadedData([]); // Clear loaded data
    setHasMoreData(true); // Reset has more data flag

    // skipCache: setPageCache above hasn't landed yet when loadInitialData
    // runs synchronously after it, so without this the very first refetch
    // would still read the stale (pre-clear) cache and return old data.
    loadInitialData({ skipCache: true }).then(() => {
      if (currentPageBeforeRefresh > 0) {
        setTimeout(() => {
          setCurrentPage(currentPageBeforeRefresh);
        }, 100);
      }
    });
  };

  // Don't double-count a tab's own status as an "advanced filter" — the tab
  // row already shows which one is active.
  const activeFilterCount = countActiveFiltersAndSort(sortDirection, selectedTypes);

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <div style={{ marginBottom: 20 }}>
        <PageHeader
          title="Orders"
          subtitle="View and manage every order you've placed."
        />
      </div>

      <div
        style={{
          display: "flex",
          gap: 12,
          alignItems: "flex-start",
          border: "1px solid rgba(232, 184, 75, 0.24)",
          background: "rgba(232, 184, 75, 0.08)",
          borderRadius: 12,
          padding: "12px 14px",
          marginBottom: 16,
        }}
      >
        <AlertTriangle size={18} style={{ color: "#e8b84b", flexShrink: 0, marginTop: 1 }} />
        <div style={{ color: "#f2f4f5", fontSize: 13, lineHeight: 1.5 }}>
          <strong style={{ color: "#fff" }}>Price lock:</strong>{" "}
          Your quoted price holds for 48 hours after approval. Create your shipment within that window — orders created late may be repriced at
          current market rates.
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(auto-fit, minmax(${isMobile ? "145px" : "180px"}, 1fr))`,
          gap: isMobile ? 12 : 16,
          marginBottom: 20,
        }}
      >
        <StatCard
          label="TOTAL ORDERS"
          value={orderStats ? orderStats.totalOrders : "—"}
          accent="#00ADB5"
          detail="all statuses"
        />
        <StatCard
          label="TOTAL VALUE"
          value={orderStats ? formatNumberToDollar(orderStats.totalValue) : "—"}
          accent="#e8b84b"
          detail="across all orders"
        />
        <StatCard
          label="PENDING APPROVAL"
          value={orderStats ? orderStats.pendingApprovalCount : "—"}
          accent="#af58f2"
          detail="need a decision"
        />
        <StatCard
          label="UNITS"
          value={orderStats ? orderStats.units : "—"}
          accent="#4caf50"
          detail="across all orders"
        />
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: isMobile ? "stretch" : "center",
          flexWrap: "wrap",
          marginBottom: 20,
          gap: isMobile ? 10 : 0,
        }}
      >
        <div style={{ display: "flex", flexWrap: "wrap", justifyContent: isMobile ? "center" : "flex-start", marginBottom: 8, width: isMobile ? "100%" : "auto" }}>
          {TAB_OPTIONS.map((tab, index) => {
            const active = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => handleTabClick(tab)}
                style={{
                  marginLeft: index === 0 ? 0 : 8,
                  marginBottom: 8,
                  padding: "6px 14px",
                  borderRadius: 999,
                  fontSize: 11.5,
                  fontWeight: 700,
                  cursor: "pointer",
                  border: active ? "1px solid rgba(0,173,181,0.4)" : "1px solid #2a2a2e",
                  background: active ? "rgba(0,173,181,0.16)" : "#1e1e1e",
                  color: active ? "#00ADB5" : "#c3c8cc",
                }}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        <div
          style={{
            display: "flex",
            alignItems: isMobile ? "stretch" : "center",
            flexWrap: "wrap",
            marginBottom: 8,
            width: isMobile ? "100%" : "auto",
            gap: isMobile ? 10 : 0,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", flex: isMobile ? "1 1 0" : "0 0 auto", minWidth: 0, gap: isMobile ? 8 : 12 }}>
            {!isMobile && <RefreshButton onRefresh={handleRefresh} />}
            <div style={{ flex: isMobile ? 1 : "none", minWidth: 0 }}>
              <SearchBar
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                setDebouncedTerm={setDebouncedSearch}
              />
            </div>
          </div>

          <div style={{ marginLeft: isMobile ? 0 : 20, flexShrink: 0 }}>
            <DropdownMenu open={filterOpen} onOpenChange={handleFilterOpenChange}>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Filter size={13} style={{ marginRight: 6 }} />
                  Filter & Sort
                  {activeFilterCount > 0 && (
                    <span
                      style={{
                        marginLeft: 6,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minWidth: 16,
                        height: 16,
                        borderRadius: 8,
                        background: "#00ADB5",
                        color: "#000",
                        fontSize: 10,
                        fontWeight: 800,
                        padding: "0 4px",
                      }}
                    >
                      {activeFilterCount}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="tw-w-64">
                <DropdownMenuLabel>Filter by Type</DropdownMenuLabel>
                {TYPE_OPTIONS.map((option) => (
                  <DropdownMenuCheckboxItem
                    key={option.value}
                    checked={selectedTypes.includes(option.value)}
                    onSelect={(e) => e.preventDefault()}
                    onCheckedChange={(checked) => handleTypeChange(option.value, checked)}
                  >
                    {option.label}
                  </DropdownMenuCheckboxItem>
                ))}

                <DropdownMenuSeparator />

                <DropdownMenuItem active={sortDirection === "ASC"} onClick={handleSortAscending}>
                  <ArrowUpNarrowWide size={14} style={{ marginRight: 8 }} />
                  Sort by Price (Low to High)
                </DropdownMenuItem>
                <DropdownMenuItem active={sortDirection === "DESC"} onClick={handleSortDescending}>
                  <ArrowDownWideNarrow size={14} style={{ marginRight: 8 }} />
                  Sort by Price (High to Low)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      <DataTable
        columns={columns}
        rows={filteredData}
        nestedColumns={itemsColumns}
        nestedRowKey="newOrderItems"
        nestedTableHeader="Order Items"
        totalRows={totalRows}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        onPageChange={(newPage) => handleChangePage(null, newPage)}
        onRowsPerPageChange={(newValue) =>
          handleChangeRowsPerPage({ target: { value: newValue } })
        }
        loading={loadingChunk || isNavigatingToPage}
        minWidth={945}
      />
    </div>
  );
};

export default MemberOpenOrderPage;
